<template>
  <div
    ref="sidebar"
    :class="['sidebar', position, { 'is-visible': internalVisible }]"
    :style="sidebarStyle"
  >
    <div
      class="sidebar-header"
      ref="sidebarHeader"
    >
      <h3>{{ title }}</h3>
      <button
        @click="closeSidebar"
        class="close-btn"
      >
        <span class="fa fa-remove" />
      </button>
    </div>
    <div
      class="sidebar-content"
      ref="sidebarContent"
    >
      <component
        v-if="contentComponent"
        :is="contentComponent"
        :props="props"
      />
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue'

export default {
  name: 'SideBar',
  props: {
    isVisible: Boolean,
    title: String,
    position: String,
    componentName: String,
    level: Number,
    props: Object,
  },
  data() {
    return {
      topOffset: 0,
      contentComponent: null,
      internalVisible: false,
      sidebarWidth: 300,
      asideWidth: 0,
      resizeObserver: null,
      allSidebarWidths: [],
    };
  },
  computed: {
    sidebarStyle() {
      const offset = this.calculateRightOffset()
      const basePosition = this.position === 'left' ? this.asideWidth : 0;
      return {
        top: `${this.topOffset}px`,
        minWidth: '300px',
        zIndex: 1000 - this.level,
        [this.position]: `${basePosition + offset}px`,
        transform: this.internalVisible ? 'translateX(0)' : this.position === 'right' ? 'translateX(100%)' : 'translateX(-100%)',
      };
    },
  },
  watch: {
    isVisible: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.internalVisible = false;
          // Dodaj klasę sidebar-open na mobilnych
          if (window.innerWidth <= 768) {
            document.body.classList.add('sidebar-open');
          }
          setTimeout(() => {
            this.internalVisible = true;
            this.updateSidebarWidth();
          }, 10);
        } else {
          this.internalVisible = false;
          // Usuń klasę sidebar-open
          document.body.classList.remove('sidebar-open');
        }
      },
    },
    componentName: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadComponent(newVal);
        } else {
          this.contentComponent = null;
        }
      },
    },
  },
  methods: {
    calculateRightOffset() {
      return this.allSidebarWidths.slice(0, this.level).reduce((total, width) => total + width, 0);
    },
    updateAllSidebarWidths() {
      const sidebars = document.querySelectorAll('.sidebars-container .sidebar');
      this.allSidebarWidths = Array.from(sidebars).map((sidebar) => sidebar.offsetWidth || 300);
    },
    closeSidebar(level = 0) {
      this.$emit('close', this.level || level);
      this.emitter.emit('closeSidebar');
      // Usuń klasę sidebar-open przy zamykaniu
      document.body.classList.remove('sidebar-open');
    },
    updateTopOffset() {
      const header = document.getElementById('kt_header');
      this.topOffset = header ? header.offsetHeight : 0;
    },
    updateSidebarWidth() {
      this.sidebarWidth = this.$refs.sidebar ? this.$refs.sidebar.offsetWidth : 300;
    },
    updateAsideWidth() {
      const aside = document.getElementById('kt_aside');
      this.asideWidth = aside ? aside.offsetWidth : 0;
    },
    async loadComponent(name) {
      try {
        const component = await import(`@/components/content/_sideBar/${name}.vue`);
        this.contentComponent = markRaw(component.default);
      } catch (error) {
        this.contentComponent = null;
      }
    },
    observeAsideWidth() {
      const aside = document.getElementById('kt_aside');
      if (aside) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateAsideWidth();
        });
        this.resizeObserver.observe(aside);
      }
    },
    updateContentHeight() {
      const windowHeight = window.innerHeight;
      const headerHeight = document.getElementById('kt_header') ? document.getElementById('kt_header').offsetHeight : 0;
      const sidebarHeaderHeight = this.$refs.sidebarHeader ? this.$refs.sidebarHeader.offsetHeight : 0;
      const paddingBottom = 15;
      const contentHeight = windowHeight - headerHeight - sidebarHeaderHeight - paddingBottom;
      if (this.$refs.sidebarContent) {
        this.$refs.sidebarContent.style.height = `${contentHeight}px`;
      }
    },
  },
  mounted() {
    this.updateTopOffset();
    this.updateAsideWidth();
    this.observeAsideWidth();
    this.updateContentHeight();
    this.updateAllSidebarWidths();
    window.addEventListener('resize', () => {
      this.updateSidebarWidth();
      this.updateContentHeight();
    });

  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener('resize', () => {
      this.updateSidebarWidth();
      this.updateContentHeight();
    });
  },
};
</script>

<style scoped>
.sidebar {
  position: fixed;
  top: 0;
  bottom: 0;
  min-width: 300px;
  background-color: #2a5ca3;
  color: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  transition: transform 0.3s ease;
}

@media (max-width: 768px) {
  .sidebar {
    top: 65px;
    width: 100vw !important;
    min-width: 100vw !important;
    height: 55vh !important;
    bottom: auto !important;
    max-height: 55vh;
    border-radius: 0;
    z-index: 1001;
  }

  .sidebar:has(.user-menu-mobile) {
    max-height: 35vh !important;
    height: 35vh !important;
  }

  .sidebar .user-menu-mobile {
    width: 100% !important;
    max-width: none !important;
    min-width: 100% !important;
    height: auto !important;
    min-height: auto !important;
  }

  .sidebar-content:has(.user-menu-mobile) {
    padding-bottom: 0 !important;
  }
}

@media (max-width: 480px) {
  .sidebar {
    top: 55px;
    height: 50vh !important;
    max-height: 50vh;
    left: 0;
  }

  .sidebar:has(.user-menu-mobile) {
    max-height: 27vh !important;
    height: 27vh !important;
  }

  .sidebar .user-menu-mobile {
    width: 100% !important;
  }

  .sidebar-content:has(.user-menu-mobile) {
    padding-bottom: 0 !important;
  }
}

.sidebar h3{
  color: white;
  margin: 0;
}

.sidebar.is-visible {
  transform: translateX(0) !important;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: var(--primary-color);
  font-size: 18px;

  @media (max-width: 768px) {
    padding: 5px 12px;
    font-size: 12px;
    min-height: 35px;

    h3 {
      font-size: 12px !important;
      margin: 0;
      font-weight: 600;
    }
  }
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 2px;
  }
}

.sidebar-content {
  padding: 15px;
  flex: 1;
  background-color: #f5f5f5;
  color: #333;
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 5px 8px;
    overflow-y: hidden;
    flex: 1;
  }
}
</style>
